import { type ReactNode, useEffect, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';

import { Button, Flex } from '@rover/kibble/core';

type Props = {
  /** Left false to draw the header row without the mode button. */
  canManage?: boolean;
  isManaging: boolean;
  /** Set when the controls sit on a dark background, as they do in the lightbox. */
  contrast?: boolean;
  /** Drawn opposite the mode button, so the host's header shares this row. */
  gridHeader?: ReactNode;
  /** Drawn after the mode button, as the lightbox does with its close button. */
  headerEnd?: ReactNode;
  onEnterManageMode: () => void;
  onCancel: () => void;
};

const ImageGridManageControls = ({
  canManage = true,
  isManaging,
  contrast = false,
  gridHeader,
  headerEnd,
  onEnterManageMode,
  onCancel,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const headerRef = useRef<HTMLElement>(null);
  const manageRef = useRef<HTMLElement>(null);
  const doneRef = useRef<HTMLElement>(null);

  const shownButton = (): 'done' | 'manage' | undefined => {
    if (isManaging) return 'done';
    return canManage ? 'manage' : undefined;
  };
  const modeButtonShown = shownButton();
  const lastModeButtonShown = useRef(modeButtonShown);

  // Replacing or removing the mode button that has focus drops focus to the page.
  useEffect(() => {
    const lastShown = lastModeButtonShown.current;
    lastModeButtonShown.current = modeButtonShown;
    if (!lastShown || lastShown === modeButtonShown) return;
    const page = headerRef.current?.ownerDocument;
    if (!page || page.activeElement !== page.body) return;

    const modeButtonRefs = { done: doneRef, manage: manageRef };
    (modeButtonShown ? modeButtonRefs[modeButtonShown] : headerRef).current?.focus?.();
  }, [modeButtonShown]);

  const modeButton = (button: JSX.Element): JSX.Element => (
    <Flex
      data-testid="gallery-header"
      htmlRef={headerRef}
      webProps={{ tabIndex: -1 }}
      flexDirection="row"
      width="100%"
      flexWrap="wrap"
      alignItems="center"
      gap="3x"
    >
      {gridHeader}
      <Flex flexDirection="row" flex="1" justifyContent="flex-end" gap="3x">
        {canManage && button}
        {headerEnd}
      </Flex>
    </Flex>
  );

  // One tree in both modes, so the header keeps its focus when the mode changes.
  return modeButton(
    isManaging ? (
      <Button
        key="done"
        variant="flat"
        size="small"
        contrast={contrast}
        onPress={onCancel}
        webProps={{ ref: doneRef }}
      >
        {t`Done`}
      </Button>
    ) : (
      <Button key="manage" size="small" onPress={onEnterManageMode} webProps={{ ref: manageRef }}>
        {t`Manage photos`}
      </Button>
    )
  );
};

export default ImageGridManageControls;
